import type { ReactNode } from 'react';
import type { SkirtBest } from './heat_savings.js';
import { tiersTone } from './heat_tone.js';
import { TravelCalendar } from '../../shared/travel_calendar.js';
import type { FlightPlannerCopy } from './planner_copy.js';
import { LegStatus, PlannerStatus } from './planner_status.js';
import type { PlannerState } from './planner_state.js';
import type { BinaryPlanner } from './binary_planner.js';
import type { MultiPlanner } from './multi_planner.js';

/*
 * As grades de datas do planner: a do binário e a da perna em edição do
 * multi. Saíram de `planner_form.tsx` pelo teto de linhas dele.
 */

export function BinaryCalendarView({
  binary,
  readOnly,
  loading,
  choice,
  best,
  onPick,
  sheet,
  copy,
  locale,
}: {
  readonly binary: BinaryPlanner;
  readonly readOnly: boolean;
  readonly loading: boolean;
  readonly choice: string;
  readonly best?: SkirtBest;
  /** Na FOLHA estreita (#3446) o calendário vem em `layout="sheet"` — as setas
   *  e o rodapé pregados são dele —, e a saia de estado vai no rodapé (`footer`)
   *  em vez de seguir a grade. Ausente ⇒ popover. */
  readonly sheet?: ReactNode;
  /** Sobrepõe o pick da grade — é como o layout aberto FECHA o popover no clique
   *  do dia em somente ida (#2136): o wrapper chama `binary.pick` e decide se a
   *  camada fecha. Ausente, o pick cru de sempre. */
  readonly onPick?: (date: string) => void;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
}) {
  const pick = onPick ?? binary.pick;
  const { out, ret } = binary;
  /* O `onChange` do calendário traduzido para o `pick` do planner: a data que
     MUDOU é a tocada. No período, a ida escolhida e o fim chegando é a volta;
     qualquer outra forma é uma ida nova (o reinício do par, ou o primeiro
     toque). Antes da ida, na fase da volta, os dias estão sob o piso e não
     chegam aqui. */
  const onChange = (next: { from?: string; to?: string }) => {
    if (binary.oneWayTab) {
      if (next.from) pick(next.from);
      return;
    }
    if (out && !ret && next.from === out && next.to) pick(next.to);
    else if (next.from) pick(next.from);
  };
  const calendar = (
    <TravelCalendar
      {...(binary.oneWayTab ? { mode: 'single' as const } : { minNights: 0 as const })}
      value={binary.oneWayTab ? { from: out } : { from: out, to: ret }}
      onChange={onChange}
      locale={locale}
      previousMonthLabel={copy.prevMonth}
      nextMonthLabel={copy.nextMonth}
      minDate={binary.floorIso}
      // Pré-rota a grade é INERTE (congelada como o card `readOnly`): a saia
      // diz o que destrava.
      readOnly={readOnly || !binary.routeOk}
      busy={loading}
      selectionTags={{ start: copy.outboundTag, end: copy.returnTag }}
      {...(sheet === undefined
        ? { dayTone: binary.dayTone, month: binary.month, onMonthChange: binary.onMonthChange }
        : {
            layout: 'sheet' as const,
            dayTone: binary.sheet.dayTone,
            onMonthsChange: binary.sheet.onMonthsChange,
            footer: sheet,
          })}
    />
  );
  /* `.fpl-cal`: o marcador do bloco do calendário que os robôs do e2e e o
     `aria-busy` dos testes leem — sem ele nem o popover nem a folha têm um nó
     que diga "aqui está a grade". Na folha ele é a coluna flex que o
     `TravelCalendar` mede (`flex-1`). */
  if (sheet !== undefined)
    return <div className="fpl-cal flex min-h-0 flex-1 flex-col">{calendar}</div>;
  return (
    <>
      <div className="fpl-cal">{calendar}</div>
      <PlannerStatus
        choice={choice}
        loading={loading}
        loadingLabel={copy.loadingPrices}
        {...(best ? { best } : {})}
        {...(binary.showCalMeta ? { meta: copy.analyzed({ count: binary.analyzed }) } : {})}
        {...(binary.noHeat ? { notice: copy.noPriceTrend } : {})}
      />
    </>
  );
}

export function LegCalendarView({
  state,
  multi,
  readOnly,
  loading,
  sheet,
  copy,
  locale,
}: {
  readonly state: PlannerState;
  readonly multi: MultiPlanner;
  readonly readOnly: boolean;
  readonly loading: boolean;
  /** Na FOLHA estreita (#3446) o calendário vem em `layout="sheet"`, sem
   *  rodapé: a linha do multi grava no toque. */
  readonly sheet: boolean;
  readonly copy: FlightPlannerCopy;
  readonly locale: string;
}) {
  if (!multi.editingLeg) return null;
  /* A grade do modo MULTI: uma data só, piso encadeado, e o calor **da rota
     desta perna** — não o do bloco. Um heatmap é por ROTA e o multi tem uma
     rota por trecho, então cada perna pede o seu (ver `legHeatLastAsked` no
     card). Enquanto o dele não chegou — ou quando o provider não tem tendência
     pra aquela rota — as células ficam neutras e CLICÁVEIS, e a saia diz "sem
     tendência de preço". O que nunca acontece é pintar os tiers de uma perna
     em outra. */
  const calendar = (
    <TravelCalendar
      key={state.editingLegId}
      mode="single"
      value={{ from: multi.editingLeg.date }}
      onChange={(next) => {
        if (next.from) multi.changeLeg(multi.editingLegIndex, { date: next.from });
      }}
      locale={locale}
      previousMonthLabel={copy.prevMonth}
      nextMonthLabel={copy.nextMonth}
      minDate={multi.legFloor}
      readOnly={readOnly}
      busy={loading}
      {...(sheet
        ? {
            layout: 'sheet' as const,
            dayTone: multi.sheet.dayTone,
            onMonthsChange: multi.sheet.onMonthsChange,
          }
        : {
            // O heat do multi é endereçado por ROTA (`routeHeat`/`legHeatKey`),
            // então `legTiers` já é o desta perna — o que falta saber é só se
            // há algo a pintar.
            dayTone: tiersTone(multi.legTiers, loading && multi.legTiers.size === 0),
            month: multi.legWindowFrom,
            onMonthChange: multi.pageLegMonths,
          })}
    />
  );
  if (sheet) return <div className="fpl-cal flex min-h-0 flex-1 flex-col">{calendar}</div>;
  return (
    <>
      <div className="fpl-cal">{calendar}</div>
      <LegStatus
        leg={multi.editingLeg}
        legNumber={multi.legNumber}
        bestDate={multi.legBestDate}
        analyzed={multi.legTiers.size}
        loading={loading}
        copy={copy}
        locale={locale}
      />
    </>
  );
}
